import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContextLazy'
import QRCode from 'qrcode'
import { ShieldCheckIcon, KeyIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

export const MFASetup: React.FC = () => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [mfaStatus, setMfaStatus] = useState({ enrolled: false, verified: false })
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  
  const { enrollMFA, verifyMFA, unenrollMFA, getMFAStatus } = useAuth()

  useEffect(() => {
    checkMFAStatus()
  }, [])

  const checkMFAStatus = async () => {
    const status = await getMFAStatus()
    setMfaStatus(status)
  }

  const handleEnrollMFA = async () => {
    setError(null)
    setLoading(true)
    
    const { error, qrCode, secret: mfaSecret } = await enrollMFA()
    
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    if (qrCode && mfaSecret) {
      // Generate QR code image
      try {
        const qrCodeDataUrl = await QRCode.toDataURL(qrCode)
        setQrCodeUrl(qrCodeDataUrl)
        setSecret(mfaSecret)
      } catch (err) {
        setError('Failed to generate QR code')
      }
    }
    
    setLoading(false)
  }

  const handleVerifyMFA = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    
    const { error } = await verifyMFA(verificationCode)
    
    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }
    
    setSuccess(true)
    setLoading(false)
    setTimeout(() => {
      checkMFAStatus()
      setQrCodeUrl('')
      setSecret('')
      setVerificationCode('')
      setSuccess(false)
    }, 2000)
  }

  const handleDisableMFA = async () => {
    setError(null)
    setLoading(true)
    
    const { error } = await unenrollMFA()
    
    if (error) {
      setError(error.message)
    } else {
      setShowDisableConfirm(false)
      checkMFAStatus()
    }
    
    setLoading(false)
  }

  if (mfaStatus.enrolled && !qrCodeUrl) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
              <p className="text-sm text-green-600 mt-1">Enabled</p>
            </div>
          </div>
          <button
            onClick={() => setShowDisableConfirm(true)}
            className="text-sm text-red-600 hover:text-red-700 font-medium"
          >
            Disable 2FA
          </button>
        </div>

        {showDisableConfirm && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-800 mb-3">
              Are you sure you want to disable two-factor authentication? This will make your account less secure.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDisableMFA}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {loading ? 'Disabling...' : 'Yes, Disable 2FA'}
              </button>
              <button
                onClick={() => setShowDisableConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center mb-6">
        <ShieldCheckIcon className="h-8 w-8 text-amber-500 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-600 mt-1">
            Add an extra layer of security to your account
          </p>
        </div>
      </div>

      {!qrCodeUrl ? (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Two-factor authentication adds an additional layer of security to your account by requiring a code from your authenticator app when signing in.
          </p>
          <button
            onClick={handleEnrollMFA}
            disabled={loading}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium transition-colors"
          >
            {loading ? 'Setting up...' : 'Enable 2FA'}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Step 1: Scan QR Code</h4>
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            <div className="flex justify-center mb-4">
              <img src={qrCodeUrl} alt="2FA QR Code" className="border-2 border-gray-200 rounded-lg" />
            </div>
            <details className="mb-4">
              <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-700">
                Can't scan? Enter this code manually
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <code className="text-xs text-gray-800 break-all">{secret}</code>
              </div>
            </details>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Step 2: Enter Verification Code</h4>
            <form onSubmit={handleVerifyMFA} className="space-y-4">
              <div>
                <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter the 6-digit code from your authenticator app
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="verification-code"
                    type="text"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:bg-white transition-all duration-200 sm:text-sm"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              {success && (
                <div className="flex items-center text-green-600">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">2FA enabled successfully!</span>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium transition-colors"
                >
                  {loading ? 'Verifying...' : 'Verify and Enable'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setQrCodeUrl('')
                    setSecret('')
                    setVerificationCode('')
                    setError(null)
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}